import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Palette,
  ShieldCheck,
  Sparkles,
  Layers,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  FileCheck,
  Layout,
  Tv,
  Printer,
  Radio,
  Eye,
  Search,
  Share2,
  Mail,
  Globe,
  Monitor,
  Video,
  PenTool,
  Grid,
  Camera,
  BookOpen,
} from "lucide-react";

export const Route = createFileRoute("/services_/visual-identity")({
  head: () => ({
    meta: [
      {
        title:
          "Visual Identity & Brand Guidelines — KMWAdvertising LLP | Consistent Brand Ecosystems",
      },
      {
        name: "description",
        content:
          "Build a Consistent Brand Across Every Platform with Visual Identity & Brand Guidelines Services by KMWAdvertising LLP. Typography, color palettes, tone of voice, and multi-channel creative systems.",
      },
      {
        property: "og:title",
        content: "Visual Identity & Brand Guidelines — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Protect your brand consistency and create performing creative across TV, print, outdoor, digital, and web platforms.",
      },
    ],
  }),
  component: VisualIdentityServicePage,
});

export function VisualIdentityServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const visualElements = [
    "Colour Palette",
    "Typography",
    "Iconography",
    "Photography Style",
    "Illustration Style",
    "Graphic Elements",
    "Layout Systems",
    "Brand Patterns",
    "Presentation Templates",
    "Social Media Templates",
  ];

  const guidelineStandards = [
    "Logo Usage Rules",
    "Colour Specifications",
    "Typography Standards",
    "Photography Direction",
    "Tone of Voice",
    "Illustration Style",
    "Social Media Templates",
    "Advertising Standards",
    "Print Specifications",
    "Digital Guidelines",
  ];

  const channelPerformanceList = [
    "Television Advertising",
    "Print Advertising",
    "Outdoor Media",
    "Cinema Advertising",
    "Google Ads",
    "Meta Ads",
    "LinkedIn Campaigns",
    "Programmatic Advertising",
    "OTT Campaigns",
    "Social Media Marketing",
    "Email Marketing",
    "Websites",
    "Landing Pages",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Visual Identity</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Palette className="h-3.5 w-3.5" />
              Visual Identity & Brand Systems
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Visual Identity
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Build a Consistent Brand Across Every Platform
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Your visual identity extends beyond your logo. It includes every design element customers encounter across physical and digital touchpoints.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                A consistent visual identity strengthens recognition and creates a more professional customer experience.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#consultation-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Develop Visual Identity System</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#brand-guidelines"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Brand Guidelines</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Design Elements Section */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Grid className="h-3.5 w-3.5" />
              Comprehensive Visual Design Elements
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Every Design Element Customers Encounter
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              We design holistic visual systems that establish emotional resonance, brand cohesion, and seamless user experiences everywhere your audience interacts with your business.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {visualElements.map((element) => (
              <div
                key={element}
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition flex flex-col justify-between"
              >
                <div className="h-8 w-8 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 mb-3">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-bold text-white">{element}</span>
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-pink-300/90 italic font-semibold">
            A consistent visual identity strengthens recognition and creates a more professional customer experience.
          </p>
        </div>
      </section>

      {/* Brand Guidelines Section */}
      <section id="brand-guidelines" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
                <BookOpen className="h-3.5 w-3.5" />
                Brand Guidelines
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Protect Your Brand Consistency
              </h2>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                As organisations grow, maintaining consistency becomes increasingly important. Our Brand Guidelines specify strict visual and communication rules so internal teams, agencies, and partners deliver unified brand experiences.
              </p>

              <div className="space-y-2.5">
                <span className="text-xs font-mono font-bold uppercase tracking-wider text-purple-300 block mb-2">
                  Our Brand Guidelines Typically Include:
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {guidelineStandards.map((item) => (
                    <div
                      key={item}
                      className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-center gap-2.5"
                    >
                      <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                      <span className="text-xs font-semibold text-white">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-slate-400 italic pt-2">
                These standards ensure every communication reflects your brand accurately regardless of who creates it.
              </p>
            </div>

            <div className="lg:col-span-6">
              <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-tr from-purple-950/30 via-[#0d0d1a] to-pink-950/30 border border-white/15 relative overflow-hidden space-y-6">
                <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-purple-500 opacity-20" />

                <h3 className="text-xl font-bold text-white relative z-10">
                  Single Source of Truth for Your Brand
                </h3>

                <blockquote className="text-slate-300 text-sm italic border-l-2 border-purple-400 pl-4 py-1 leading-relaxed relative z-10">
                  "These standards ensure every communication reflects your brand accurately regardless of who creates it."
                </blockquote>

                <div className="space-y-3 text-xs text-slate-400 leading-relaxed relative z-10 pt-2 border-t border-white/10">
                  <p>
                    Clear guidelines prevent brand dilution, streamline onboarding for new team members, and eliminate design ambiguity across global marketing efforts.
                  </p>
                  <p>
                    KMWAdvertising LLP provides both print-ready brand manuals and interactive digital brand portals for real-time asset access.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creative That Performs Across Every Channel */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Omnichannel Creative Strategy
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Creative That Performs Across Every Channel
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Unlike traditional design studios, KMWAdvertising LLP combines creativity with media expertise.
            </p>
          </div>

          <div className="p-8 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6 mb-8">
            <h3 className="text-base font-mono font-bold uppercase tracking-wider text-rose-300">
              Every creative asset is developed with its intended platform in mind, including:
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
              {channelPerformanceList.map((ch) => (
                <div
                  key={ch}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 text-center text-slate-200 font-medium hover:border-rose-500/30 transition"
                >
                  {ch}
                </div>
              ))}
            </div>
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-rose-950/30 via-pink-950/20 to-purple-950/30 border border-white/15">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              Because our creative team works alongside media planners and performance marketers, every design is developed to maximise engagement, improve campaign effectiveness and support measurable business objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Brief & Form */}
      <section id="consultation-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Visual Identity Session
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Build a Unified Visual Identity System
              </h2>
              <p className="text-slate-300 text-sm">
                Connect with our brand strategists to audit your current visual assets or build a complete identity system from scratch.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Identity Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our lead brand consultant will reach out within 24 hours to schedule your strategy consultation.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Radhika Roy"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Stellar Dynamics"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="radhika@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Primary Service Needed</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="identity">Complete Visual Identity System</option>
                    <option value="guidelines">Brand Guidelines & Standards Manual</option>
                    <option value="omnichannel">Omnichannel Creative Campaign Assets</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Vision / Objectives</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your brand goals, target channels, or existing visual challenges..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Visual Identity Consultation</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
